import { ButtonLink, Label } from '../ExpandableLink/ExpandableLink.style';
import { Fragment, FunctionalComponent, h } from 'preact';
import { Service, isPrivacyButtonUI, isTcfLabels, isTcfUI } from '@usercentrics/cmp-browser-sdk';
import ExpandableCard from '../ExpandableCard/ExpandableCard';
import { RefContainer } from './VirtualList.style';
import { ScrollToId } from '../../App.helpers';
import { ServiceName } from '../ServicesListItem/Service.styles';
import ServicesListItem from '../ServicesListItem';
import SubServicesInfo from '../SubServices/SubServicesInfo';
import { TabPanelTitle } from '../Tabs';
import { UICategory } from '../../contexts/CategoryContext';
import { VerticalSpacer } from '../Spacers/VerticalSpacer';
import { memo } from 'preact/compat';
import { useRef } from 'preact/hooks';
import { useSettingsContext } from '../../contexts/SettingsContext';
import { useVirtualListItem } from '../../hooks';

type ItemProps = {
  categories: UICategory[];
  getItemSize: (index: number) => number;
  hasTopSpace?: boolean;
  hideToggle: boolean;
  id: number;
  index: number;
  onOpenSubServiceDetail: (subService: Service) => void;
  onToggleService: (checked: boolean, id: string) => void;
  onToggleAllServices?: () => void;
  services: Service[];
  setItemSize: (index: number, height: number) => void;
  showToggles?: boolean;
  title?: string;
  scrollToId?: ScrollToId;
};

const VirtualServiceItem: FunctionalComponent<ItemProps> = memo(
  ({
    categories,
    hasTopSpace = false,
    hideToggle = false,
    id,
    index,
    onOpenSubServiceDetail,
    onToggleService,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onToggleAllServices = () => {},
    services,
    getItemSize,
    setItemSize,
    title,
    showToggles = true,
    scrollToId,
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const { updateComponentSize } = useVirtualListItem({ getItemSize, index, itemRef, setItemSize });
    const { labels, ui } = useSettingsContext();

    if (!labels || !ui || isPrivacyButtonUI(ui)) {
      return null;
    }

    const type = isTcfLabels(labels) ? labels.secondLayer.vendorsTab : labels.secondLayer.serviceTab;

    // Optional: render of a space as first item on the list
    if (hasTopSpace && index === 0) {
      return (
        <RefContainer ref={itemRef}>
          <VerticalSpacer custom={16} />
        </RefContainer>
      );
    }

    // Optional: render a title as a list item
    if (
      title != null &&
      title !== '' &&
      ((!hasTopSpace && id === 0) || (hasTopSpace && id === 1)) &&
      isTcfLabels(labels) &&
      isTcfUI(ui)
    ) {
      return (
        <RefContainer ref={itemRef}>
          <TabPanelTitle>{title}</TabPanelTitle>
          {ui.secondLayer.showToggleAllVendors && (
            <ButtonLink data-testid="uc-toggle-all-non-iab-vendors" onClick={onToggleAllServices}>
              <Label>{labels.vendor.toggleAll}</Label>
            </ButtonLink>
          )}
        </RefContainer>
      );
    }

    let serviceId = id;

    if (hasTopSpace) {
      serviceId -= 1;
    }

    if (title != null) {
      serviceId -= 1;
    }

    const service = services[serviceId];

    const onToggle = (checked: boolean): void => {
      onToggleService(checked, service.id);
    };

    const getCategoryLabel = (categorySlug: string) => {
      const tempCategory = categories.find((category) => category.slug === categorySlug);

      return (
        (
          <Fragment>
            {tempCategory && <ServiceName>{tempCategory.label}</ServiceName>}
            {service.subServicesLength > 0 && <SubServicesInfo divider="bullet" size={service.subServicesLength} />}
          </Fragment>
        ) || ''
      );
    };

    const toggles = [];

    if (showToggles) {
      toggles.push({
        ariaLabel: `${service.name} ${type} ${labels.ariaLabels.vendorConsentToggle}`,
        checked: service.consent.status,
        id: service.id,
        isDisabled: service.isEssential,
        onToggle,
      });
    }

    return (
      <RefContainer ref={itemRef}>
        <ExpandableCard
          hideToggle={hideToggle}
          label={getCategoryLabel(service.categorySlug)}
          onToggleExpandable={updateComponentSize}
          isExpandedOnInit={scrollToId === service.id}
          title={service.name}
          toggles={toggles}
          type={type}
        >
          <ServicesListItem
            service={service}
            onOpenSubServiceDetail={onOpenSubServiceDetail}
            onToggleExpandable={updateComponentSize}
          />
        </ExpandableCard>
      </RefContainer>
    );
  },
);

export default VirtualServiceItem;
